/**
 * What a hit does once it has landed. AD-007d's list, in order:
 *
 * > "component HP model — engine, fuel tank, left/right wing cells,
 * > tail/control surfaces, pilot. Gunfire/flak distribute hits by aspect;
 * > effects: engine sputter/fire, control response degradation, lift loss,
 * > fuel leak, wound → bleed-out timer where only landing saves the career
 * > pilot."
 *
 * The five airframe components go straight into `flight/damage.ts`'s existing
 * model — which already turns them into lift loss (`structuralLiftFactor`),
 * asymmetric roll (`damageRollBias`), control degradation and engine power loss
 * — so nothing here re-implements a damage effect. The two things it does own
 * are the pilot (wound or death) and fire, because neither is a component.
 *
 * Fire deliberately lives *outside* `SimState`: the flight core has no concept
 * of burning, and giving it one would have changed the snapshot layout for
 * something only an encounter can start. It is per-tick damage plus a flag.
 */

import { applyComponentDamage, type ComponentHit } from "../flight/damage.ts";
import type { RngBank } from "../flight/rng.ts";
import { applyWound, killPilot, type SimState } from "../flight/state.ts";
import type { Tunables } from "../flight/tunables.ts";
import { combatEvent, type CombatEvent } from "./events.ts";
import type { RoundHit } from "./hit-allocation.ts";
import { isComponent } from "./hit-allocation.ts";

export interface FireState {
  readonly burning: boolean;
  readonly burningSeconds: number;
}

export const NOT_BURNING: FireState = { burning: false, burningSeconds: 0 };

export interface DamageOutcome {
  readonly sim: SimState;
  readonly fire: FireState;
  readonly events: readonly CombatEvent[];
}

/** Does this hit start a fire? Engine and fuel-tank hits only. */
function ignites(hit: RoundHit, rng: RngBank, tunables: Tunables): boolean {
  if (hit.component === "engine") return rng.chance("gunnery", tunables.engineFireProbabilityPerHit);
  if (hit.component === "fuel") return rng.chance("gunnery", tunables.fuelFireProbabilityPerHit);
  return false;
}

interface PilotOutcome {
  readonly sim: SimState;
  readonly killed: boolean;
}

function applyPilotHit(sim: SimState, rng: RngBank, tunables: Tunables): PilotOutcome {
  if (rng.chance("gunnery", tunables.pilotHitLethalProbability)) {
    return { sim: killPilot(sim), killed: true };
  }
  return { sim: applyWound(sim), killed: false };
}

export interface ApplyHitsInput {
  readonly sim: SimState;
  readonly fire: FireState;
  readonly hits: readonly RoundHit[];
  readonly rng: RngBank;
  /** False under AIRCRAFT MAY BE DAMAGED off: the aircraft is invulnerable. */
  readonly damageable: boolean;
  readonly aircraftId: string;
}

/**
 * Applies every round that connected this tick. Component hits are batched into
 * one `applyComponentDamage` call so the wing-failure check runs once; the
 * pilot and fire rolls happen per hit, in hit order, on the `gunnery` stream.
 */
export function applyHits(input: ApplyHitsInput): DamageOutcome {
  const { sim, hits, rng } = input;
  if (hits.length === 0 || !input.damageable) return { sim, fire: input.fire, events: [] };

  const tunables = sim.config.tunables;
  const events: CombatEvent[] = [];
  const components: ComponentHit[] = [];
  let burning = input.fire.burning;
  let wounded = sim;

  for (const hit of hits) {
    if (isComponent(hit.component)) {
      components.push({ component: hit.component, amount: hit.amount });
      if (!burning && ignites(hit, rng, tunables)) burning = true;
      continue;
    }
    const outcome = applyPilotHit(wounded, rng, tunables);
    wounded = outcome.sim;
  }

  const engineBefore = sim.damage.engine;
  const damage = applyComponentDamage(wounded.damage, components);
  const source = hits[0]?.shooterId ?? null;
  events.push(combatEvent("takingHits", input.aircraftId, source));
  if (engineBefore >= tunables.engineSputterHealth && damage.engine < tunables.engineSputterHealth) {
    events.push(combatEvent("engineSputtering", input.aircraftId, source));
  }
  if (burning && !input.fire.burning) events.push(combatEvent("onFire", input.aircraftId, source));

  return {
    sim: { ...wounded, damage },
    fire: { burning, burningSeconds: input.fire.burningSeconds },
    events,
  };
}

/**
 * A fire eats the aeroplane: the engine first (power fades, then dies) and the
 * wing structure behind it, which is what turns "on fire" into the burning
 * descent the manual's parachute-less career mode makes lethal.
 */
export function advanceFire(sim: SimState, fire: FireState, dt: number): DamageOutcome {
  if (!fire.burning) return { sim, fire, events: [] };
  const tunables = sim.config.tunables;
  const structure = tunables.fireStructureDamagePerSecond * dt;
  const damage = applyComponentDamage(sim.damage, [
    { component: "engine", amount: tunables.fireEngineDamagePerSecond * dt },
    { component: "wingLeft", amount: structure },
    { component: "wingRight", amount: structure },
  ]);
  return {
    sim: { ...sim, damage },
    fire: { burning: true, burningSeconds: fire.burningSeconds + dt },
    events: [],
  };
}
